import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  Check,
  X,
  Link2,
  Unlink,
  BookOpen,
  Undo2,
  Loader2,
  ArrowDownToLine,
  Users,
  CheckCircle,
  AlertCircle,
  BookCheck,
  Settings,
  Landmark,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BRAND_COLOR = "#c11718";

export default function BankumsaetzePage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ total: 0, matched: 0, unmatched: 0, booked: 0 });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);

  // Config state
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Match dialog
  const [matchDialog, setMatchDialog] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestSearch, setGuestSearch] = useState("");

  // Booking
  const [bookingAll, setBookingAll] = useState(false);
  const [rematching, setRematching] = useState(false);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get("/banksapi/local-transactions", { params, timeout: 30000 });
      setTransactions(res.data.transactions || []);
      setStats(res.data.stats || { total: 0, matched: 0, unmatched: 0, booked: 0 });
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error("Fehler beim Laden:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get("/banksapi/config");
      setConfig(res.data);
    } catch (err) {
      console.error("Config error:", err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchTransactions();
    loadGuests();
  }, [fetchConfig, fetchTransactions]);

  const loadGuests = async () => {
    try {
      const res = await api.get("/guests");
      setGuests(res.data.filter(g => !g.archived).sort((a, b) =>
        `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
      ));
    } catch (err) {
      console.error(err);
    }
  };

  // ==================== ACTIONS ====================

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/banksapi/sync-transactions", null, { timeout: 120000 });
      toast.success(`Sync abgeschlossen: ${res.data.new_transactions} neue, ${res.data.auto_matched} auto-matched`);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await api.post("/banksapi/test");
      toast.success(`Verbindung OK! Tenant: ${res.data.tenant}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verbindungstest fehlgeschlagen");
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveApiKey = async () => {
    try {
      await api.put("/banksapi/config", { api_key: apiKey });
      toast.success("API-Key gespeichert");
      setApiKey("");
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleLoadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await api.get("/banksapi/accounts");
      setAccounts(res.data.accounts || []);
      if (res.data.accounts?.length === 0) {
        toast.info("Keine Konten gefunden. Bitte erst eine Bankverbindung hinzufuegen.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konten konnten nicht geladen werden");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSelectAccount = async (acc) => {
    try {
      await api.put("/banksapi/select-account", {
        access_id: acc.access_id,
        product_id: acc.product_id,
      });
      toast.success(`Konto ${acc.iban || acc.kontonummer} ausgewählt`);
      fetchConfig();
    } catch (err) {
      toast.error("Fehler beim Auswählen");
    }
  };

  const handleConnectBank = async () => {
    setConnecting(true);
    try {
      const res = await api.post("/banksapi/connect-bank");
      if (res.data.status === "redirect" && res.data.redirect_url) {
        const callbackUrl = encodeURIComponent(window.location.href);
        window.open(`${res.data.redirect_url}&callbackUrl=${callbackUrl}`, "_blank");
        toast.info("Bitte schliessen Sie den Bankverbindungsprozess im neuen Fenster ab.");
      } else {
        toast.success("Bankverbindung hergestellt");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verbindung fehlgeschlagen");
    } finally {
      setConnecting(false);
    }
  };

  const handleMatch = async (txId, guestId) => {
    try {
      await api.put(`/banksapi/local-transactions/${txId}/match`, { guest_id: guestId });
      toast.success("Zuordnung gespeichert");
      setMatchDialog(null);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei Zuordnung");
    }
  };

  const handleUnmatch = async (txId) => {
    try {
      await api.put(`/banksapi/local-transactions/${txId}/unmatch`);
      toast.success("Zuordnung entfernt");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleBook = async (txId) => {
    try {
      await api.post(`/banksapi/local-transactions/${txId}/book`);
      toast.success("Zahlung gebucht");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Buchen");
    }
  };

  const handleUnbook = async (txId) => {
    try {
      await api.post(`/banksapi/local-transactions/${txId}/unbook`);
      toast.success("Buchung storniert");
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Stornieren");
    }
  };

  const handleBookAll = async () => {
    if (!window.confirm("Alle zugeordneten Umsätze buchen?")) return;
    setBookingAll(true);
    try {
      const res = await api.post("/banksapi/book-all-matched");
      toast.success(`${res.data.booked} Zahlungen gebucht`);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setBookingAll(false);
    }
  };

  const handleRematch = async () => {
    setRematching(true);
    try {
      const res = await api.post("/banksapi/rematch", null, { params: { force: false }, timeout: 300000 });
      toast.success(`${res.data.rematched} von ${res.data.total_processed} zugeordnet`);
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Re-Match");
    } finally {
      setRematching(false);
    }
  };

  const handleKategorie = async (txId, kategorie) => {
    try {
      await api.put(`/banksapi/local-transactions/${txId}/kategorie`, { kategorie });
      toast.success(`Kategorie: ${kategorie}`);
      fetchTransactions();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  // ==================== HELPERS ====================

  const formatDate = (d) => {
    if (!d) return "-";
    const s = d.substring(0, 10);
    const parts = s.split("-");
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return s;
  };

  const formatAmount = (a) => {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(a || 0);
  };

  const statusBadge = (status) => {
    switch (status) {
      case "matched":
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="status-matched">Zugeordnet</Badge>;
      case "booked":
        return <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="status-booked">Gebucht</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800 border-red-200" data-testid="status-unmatched">Offen</Badge>;
    }
  };

  const confidenceBadge = (confidence, method) => {
    if (!confidence) return null;
    const color = confidence >= 90 ? "bg-green-100 text-green-700" : confidence >= 75 ? "bg-amber-100 text-amber-700" : "bg-orange-100 text-orange-700";
    return <Badge className={`${color} text-xs`}>{method} ({confidence}%)</Badge>;
  };

  const filteredGuests = guests.filter(g => {
    if (!guestSearch) return true;
    const q = guestSearch.toLowerCase();
    return `${g.first_name} ${g.last_name}`.toLowerCase().includes(q) ||
      (g.aktenzeichen || "").toLowerCase().includes(q) ||
      (g.bg_number || "").toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(total / limit);

  // ==================== RENDER ====================

  return (
    <div className="space-y-6" data-testid="bankumsaetze-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Bankumsätze</h1>
          <p className="text-sm text-slate-500 mt-1">BANKSapi Schnittstelle - Umsätze synchronisieren & zuordnen</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfig(true)}
            data-testid="config-button"
          >
            <Settings className="h-4 w-4 mr-1" />
            Einstellungen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRematch}
            disabled={rematching}
            data-testid="rematch-button"
          >
            {rematching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Re-Match
          </Button>
          <Button
            size="sm"
            onClick={handleSync}
            disabled={syncing || !config?.has_api_key}
            style={{ backgroundColor: BRAND_COLOR }}
            className="text-white"
            data-testid="sync-button"
          >
            {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowDownToLine className="h-4 w-4 mr-1" />}
            Synchronisieren
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("all"); setPage(1); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><Landmark className="h-5 w-5 text-slate-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Gesamt</p>
                <p className="text-xl font-bold" data-testid="stat-total">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("matched"); setPage(1); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50"><AlertCircle className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Zugeordnet</p>
                <p className="text-xl font-bold text-amber-700" data-testid="stat-matched">{stats.matched}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("unmatched"); setPage(1); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-50"><X className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Offen</p>
                <p className="text-xl font-bold text-red-700" data-testid="stat-unmatched">{stats.unmatched}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setStatusFilter("booked"); setPage(1); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500">Gebucht</p>
                <p className="text-xl font-bold text-green-700" data-testid="stat-booked">{stats.booked}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Suche (Name, Verwendungszweck, Bewohner...)"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="unmatched">Offen</SelectItem>
            <SelectItem value="matched">Zugeordnet</SelectItem>
            <SelectItem value="booked">Gebucht</SelectItem>
          </SelectContent>
        </Select>
        {stats.matched > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleBookAll}
            disabled={bookingAll}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="book-all-button"
          >
            {bookingAll ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BookCheck className="h-4 w-4 mr-1" />}
            Alle buchen ({stats.matched})
          </Button>
        )}
      </div>

      {/* Transaction Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center p-12 text-slate-500">
              {config?.has_api_key
                ? 'Keine Umsätze vorhanden. Klicken Sie auf "Synchronisieren" um Bankdaten abzurufen.'
                : 'Bitte konfigurieren Sie zuerst den BANKSapi API-Key unter "Einstellungen".'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-medium text-slate-600">Datum</th>
                    <th className="text-left p-3 font-medium text-slate-600">Absender</th>
                    <th className="text-left p-3 font-medium text-slate-600 hidden lg:table-cell">Verwendungszweck</th>
                    <th className="text-right p-3 font-medium text-slate-600">Betrag</th>
                    <th className="text-left p-3 font-medium text-slate-600">Bewohner</th>
                    <th className="text-center p-3 font-medium text-slate-600">Status</th>
                    <th className="text-center p-3 font-medium text-slate-600">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`tx-row-${tx.id}`}>
                      <td className="p-3 text-slate-700 whitespace-nowrap">{formatDate(tx.buchungsdatum)}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800 truncate max-w-[180px]">{tx.gegenkontoInhaber || "-"}</div>
                        {tx.gegenkontoIban && <div className="text-xs text-slate-400 truncate">{tx.gegenkontoIban}</div>}
                      </td>
                      <td className="p-3 hidden lg:table-cell">
                        <div className="text-slate-600 truncate max-w-[280px]" title={tx.verwendungszweck}>
                          {tx.verwendungszweck || "-"}
                        </div>
                      </td>
                      <td className="p-3 text-right font-semibold text-green-700 whitespace-nowrap">{formatAmount(tx.betrag)}</td>
                      <td className="p-3">
                        {tx.matched_guest_name ? (
                          <div>
                            <div className="font-medium text-slate-800">{tx.matched_guest_name}</div>
                            {confidenceBadge(tx.match_confidence, tx.match_method)}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">Nicht zugeordnet</span>
                        )}
                      </td>
                      <td className="p-3 text-center">{statusBadge(tx.status)}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          {tx.status === "unmatched" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setMatchDialog(tx); setGuestSearch(""); }}
                              className="h-7 px-2 text-xs"
                              data-testid={`match-btn-${tx.id}`}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Zuordnen
                            </Button>
                          )}
                          {tx.status === "matched" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleBook(tx.id)}
                                className="h-7 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50"
                                data-testid={`book-btn-${tx.id}`}
                              >
                                <BookOpen className="h-3 w-3 mr-1" />
                                Buchen
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setMatchDialog(tx); setGuestSearch(""); }}
                                className="h-7 px-2 text-xs"
                                title="Andere Zuordnung"
                              >
                                <RefreshCw className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUnmatch(tx.id)}
                                className="h-7 px-2 text-xs text-red-500 hover:text-red-700"
                                title="Zuordnung entfernen"
                              >
                                <Unlink className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          {tx.status === "booked" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnbook(tx.id)}
                              className="h-7 px-2 text-xs text-red-500 hover:text-red-700"
                              data-testid={`unbook-btn-${tx.id}`}
                              title="Buchung stornieren"
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              Storno
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Seite {page} von {totalPages} ({total} Einträge)</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Match Dialog */}
      <Dialog open={!!matchDialog} onOpenChange={(o) => !o && setMatchDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bewohner zuordnen</DialogTitle>
          </DialogHeader>
          {matchDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><span className="font-medium">Datum:</span> {formatDate(matchDialog.buchungsdatum)}</p>
                <p><span className="font-medium">Absender:</span> {matchDialog.gegenkontoInhaber || "-"}</p>
                <p><span className="font-medium">Betrag:</span> {formatAmount(matchDialog.betrag)}</p>
                <p className="truncate"><span className="font-medium">Zweck:</span> {matchDialog.verwendungszweck || "-"}</p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Bewohner suchen (Name, AZ, BG)..."
                  value={guestSearch}
                  onChange={e => setGuestSearch(e.target.value)}
                  className="pl-10"
                  data-testid="guest-search-input"
                  autoFocus
                />
              </div>
              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                {filteredGuests.length === 0 ? (
                  <div className="p-4 text-center text-slate-400 text-sm">Kein Bewohner gefunden</div>
                ) : (
                  filteredGuests.map(g => (
                    <button
                      key={g.id}
                      onClick={() => handleMatch(matchDialog.id, g.id)}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between"
                      data-testid={`guest-option-${g.id}`}
                    >
                      <div>
                        <span className="font-medium text-slate-800">{g.last_name}, {g.first_name}</span>
                        {g.aktenzeichen && <span className="ml-2 text-xs text-slate-400">AZ: {g.aktenzeichen}</span>}
                      </div>
                      <Users className="h-4 w-4 text-slate-300" />
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>BANKSapi Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* API Key */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">API-Key (Base64)</label>
              {config?.has_api_key && (
                <div className="flex items-center gap-2 mb-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700">Konfiguriert: {config.api_key_masked}</span>
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="API-Key eingeben..."
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  type="password"
                  data-testid="api-key-input"
                />
                <Button onClick={handleSaveApiKey} disabled={!apiKey.trim()} data-testid="save-api-key-btn">
                  Speichern
                </Button>
              </div>
            </div>

            {/* Connection Test */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleTestConnection} disabled={testingConnection || !config?.has_api_key} data-testid="test-connection-btn">
                {testingConnection ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                Verbindung testen
              </Button>
              <Button variant="outline" onClick={handleConnectBank} disabled={connecting || !config?.has_api_key} data-testid="connect-bank-btn">
                {connecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Landmark className="h-4 w-4 mr-1" />}
                Bank verbinden
              </Button>
            </div>

            {/* Account Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Konto auswählen</label>
                <Button size="sm" variant="outline" onClick={handleLoadAccounts} disabled={loadingAccounts || !config?.has_api_key} data-testid="load-accounts-btn">
                  {loadingAccounts ? <Loader2 className="h-4 w-4 animate-spin" /> : "Konten laden"}
                </Button>
              </div>
              {config?.selected_account && (
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm text-green-700">
                  <CheckCircle className="h-4 w-4" />
                  Ausgewählt: {config.selected_account.product_id}
                </div>
              )}
              {accounts.length > 0 && (
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {accounts.map((acc, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelectAccount(acc)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors"
                      data-testid={`account-option-${i}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{acc.bezeichnung || "Konto"}</span>
                          <span className="ml-2 text-sm text-slate-500">{acc.iban || acc.kontonummer}</span>
                        </div>
                        <span className="font-semibold">{formatAmount(acc.saldo)}</span>
                      </div>
                      <div className="text-xs text-slate-400">{acc.kreditinstitut} - {acc.inhaber}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
